import { postReportUploadFailureApi } from '@/shared/apis/asset-upload/post-report-upload-failure';
import { S3UploadError } from '@/shared/apis/asset-upload/s3-upload-error';
import type { UploadFailureClassification } from './upload-error-classifier';

/**
 * SSR/Node 환경 안전 UUID 생성. crypto.randomUUID 가 없으면 Math.random 기반 폴백.
 * Web Crypto 는 modern browser + Node 19+ 표준이지만 일부 SSR/구버전 호환을 위해 가드.
 */
const safeRandomUUID = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // Fallback: RFC4122 v4 유사 (엄격한 보안성보다 dedupe 키 목적)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/** SSR 환경에서 navigator 가 없는 경우 undefined 반환. */
const getUserAgent = (): string | undefined =>
  typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

/** 파일명에서 확장자 추출 (소문자, 점 제외). 확장자 없으면 undefined. */
const getFileExtension = (fileName: string): string | undefined => {
  const idx = fileName.lastIndexOf('.');
  if (idx < 0 || idx === fileName.length - 1) return undefined;
  return fileName.slice(idx + 1).toLowerCase();
};

/**
 * BE 전송 전 사용자 제공 문자열 길이 cap.
 * fileName: PII 가능 + 사용자 입력 길이 무제한 → DB/로그 적재 비대 방어.
 * errorMessage: native error stack 등 비대 가능 → 길이 cap 으로 로그 폭증 방어.
 *
 * 단위는 UTF-16 code unit (`String.length`) 기준 — UTF-8 byte 와 다름.
 * 한글 1자 = 1 code unit, UTF-8 = 3 byte. BE 컬럼 byte 길이와 정확 매칭이 필요하면
 * TextEncoder 기반 truncate 로 교체. 본 컨텍스트는 1차 가드라 char 단위 충분.
 */
const MAX_FILE_NAME_CHARS = 500;
const MAX_ERROR_MESSAGE_CHARS = 1000;
const cap = (s: string, max: number): string =>
  s.length > max ? s.slice(0, max) : s;

/**
 * BE 는 errorMessage 를 Slack 에러 카드에 그대로 싣는다.
 * 네트워크성 실패 진단이 붙어 있으면 같은 필드에 이어 붙여 원인 세분류를 노출한다.
 */
export const buildReportedErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : String(error);

  if (error instanceof S3UploadError && error.diagnostics) {
    return `${message} | diag: ${error.diagnostics}`;
  }

  return message;
};

export interface ReportUploadFailureContext {
  spaceId?: number;
  folderId?: number;
  contentId?: number;
  /** 호출 위치 식별 (예: "editor/AssetContentUpload"). BE 측 필터링·분포 분석 용도. */
  uploadPath: string;
  /**
   * 같은 업로드 시도 retry 추적용. 미지정 시 매 호출마다 새 UUID.
   * 향후 retry 추적 추가 시 호출자가 reuse 하도록 옵셔널.
   */
  clientSessionId?: string;
  /** 같은 업로드 시도 내 retry 횟수. 현재 미추적이면 undefined. */
  retryCount?: number;
}

/**
 * 분류기 결과 + 환경 메타데이터를 결합해 BE 텔레메트리로 전송한다.
 * 10개 reason 모두 `errorReason` 필드에 그대로 들어가므로 BE grouping 가능.
 *
 * 사용:
 * ```ts
 * const classified = classifyUploadFailure(error, file.name);
 * reportClassifiedUploadFailure(error, file, classified, {
 *   spaceId, folderId, uploadPath: 'editor/AssetContentUpload',
 * });
 * ```
 */
export const reportClassifiedUploadFailure = (
  error: unknown,
  file: { name: string; size?: number; type?: string },
  classified: UploadFailureClassification,
  context: ReportUploadFailureContext,
): void => {
  void postReportUploadFailureApi({
    fileName: cap(file.name, MAX_FILE_NAME_CHARS),
    fileSize: file.size,
    mimeType: file.type,
    fileExtension: getFileExtension(file.name),
    errorMessage: cap(
      buildReportedErrorMessage(error),
      MAX_ERROR_MESSAGE_CHARS,
    ),
    errorReason: classified.reason,
    i18nKey: classified.i18nKey,
    errorCode: classified.raw?.code,
    httpStatus: classified.raw?.httpStatus,
    userAgent: getUserAgent(),
    clientSessionId: context.clientSessionId ?? safeRandomUUID(),
    retryCount: context.retryCount,
    traceId: safeRandomUUID(),
    spaceId: context.spaceId,
    folderId: context.folderId,
    contentId: context.contentId,
    uploadPath: context.uploadPath,
  });
};
